import React, { useCallback, useEffect, useState } from 'react';
import './Score.css';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import { getTenantScoreAnalytics } from '../../../api/services/analyticsService';
import {
  MdShield,
  MdCheckCircle,
  MdSchedule,
  MdCancel,
  MdChevronRight,
  MdInfoOutline,
  MdStar,
  MdStarHalf,
  MdStarOutline,
} from 'react-icons/md';

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatPeriodLabel(fromMonth, fromYear, toMonth, toYear) {
  if (!fromMonth || !fromYear || !toMonth || !toYear) return null;
  const a = `${MONTH_SHORT[fromMonth - 1] || fromMonth} ${fromYear}`;
  const b = `${MONTH_SHORT[toMonth - 1] || toMonth} ${toYear}`;
  return `${a} – ${b}`;
}

function displayScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return (Math.round(n * 10) / 10).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 });
}

function scoreBand(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) {
    return { label: '—', key: 'unknown', badgeClass: 'score-badge--neutral' };
  }
  if (n >= 80) return { label: 'Excellent', key: 'excellent', badgeClass: 'score-badge--excellent' };
  if (n >= 65) return { label: 'Good', key: 'good', badgeClass: 'score-badge--good' };
  if (n >= 50) return { label: 'Fair', key: 'fair', badgeClass: 'score-badge--fair' };
  if (n >= 35) return { label: 'Needs work', key: 'low', badgeClass: 'score-badge--low' };
  return { label: 'Needs attention', key: 'risk', badgeClass: 'score-badge--risk' };
}

function renderStars(score) {
  const stars = [];
  const fullStars = Math.floor(score);
  const hasHalfStar = score % 1 !== 0;

  for (let i = 1; i <= 5; i++) {
    if (i <= fullStars) {
      stars.push(<MdStar key={i} />);
    } else if (i === fullStars + 1 && hasHalfStar) {
      stars.push(<MdStarHalf key={i} />);
    } else {
      stars.push(<MdStarOutline key={i} />);
    }
  }
  return stars;
}

function meaningCopy(bandKey) {
  switch (bandKey) {
    case 'excellent':
      return (
        <>
          An <span className="excellent-text">excellent</span> score reflects very strong on-time rent behavior in the
          period we measure. It can help with approvals, deposits, and trust with landlords and agents.
        </>
      );
    case 'good':
      return (
        <>
          A <span className="good-text">good</span> score shows solid rent behavior. Keep paying on time to move toward
          an excellent rating.
        </>
      );
    case 'fair':
      return (
        <>
          A <span className="fair-text">fair</span> score means there is room to improve—late or missed months weigh
          down your score. Focus on on-time payments going forward.
        </>
      );
    case 'low':
    case 'risk':
      return (
        <>
          This score suggests <span className="low-text">frequent late or missed rent</span> in the window we analyze.
          Improving on-time payments will lift your score over time.
        </>
      );
    default:
      return <>Your tenant score summarizes recent rent payment behavior from recorded months.</>;
  }
}

const Score = ({ onViewHistory }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('score'); // 'score' or 'ratings'
  const [ratingsPage, setRatingsPage] = useState(1);
  const RATINGS_PER_PAGE = 20;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getTenantScoreAnalytics(12);
      const payload = res?.data ?? null;
      setData(payload && typeof payload === 'object' ? payload : null);
    } catch (e) {
      setData(null);
      setError(e?.message || 'Could not load your score.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  const handleViewHistory = () => {
    if (onViewHistory) onViewHistory();
  };

  const totalMonths = Number(data?.totalMonthsCounted);
  const hasWindow = Number.isFinite(totalMonths) && totalMonths > 0;
  const tenantScore = hasWindow ? data?.tenantScore : 0;
  const band = hasWindow
    ? scoreBand(tenantScore)
    : { label: 'No score yet', key: 'unknown', badgeClass: 'score-badge--neutral' };
  const periodLabel = formatPeriodLabel(data?.fromMonth, data?.fromYear, data?.toMonth, data?.toYear);

  return (
    <div className="tenant-score-page">
      <CollapsibleTopPanel id="tenant-score-top-panel" defaultOpenOnMobile={false}>
        <div className="score-header">
          <div className="score-header-top">
            <h1 className="score-header-title">
              {activeTab === 'ratings' ? 'Ratings History' : 'TenantScore Insights'}
            </h1>
            <p className="score-header-subtitle">
              {activeTab === 'ratings'
                ? 'Reviews and feedback from your past and current landlords or agents.'
                : 'View your detailed rent behavior and public rating history.'}
            </p>
          </div>
          <div className="score-tabs">
            <button
              type="button"
              className={`score-tab-btn ${activeTab === 'score' ? 'active' : ''}`}
              onClick={() => setActiveTab('score')}
            >
              My Tenant Score
            </button>
            <button
              type="button"
              className={`score-tab-btn ${activeTab === 'ratings' ? 'active' : ''}`}
              onClick={() => { setActiveTab('ratings'); setRatingsPage(1); }}
            >
              Ratings History
            </button>
          </div>
        </div>
      </CollapsibleTopPanel>

      <div className="score-content">
        {loading && (
          <div className="score-state score-state--loading" aria-busy="true">
            <div className="score-skeleton score-skeleton--circle" />
            <div className="score-skeleton score-skeleton--badge" />
            <div className="score-skeleton score-skeleton--card" />
            <div className="score-skeleton score-skeleton--card" />
          </div>
        )}

        {!loading && error && (
          <div className="score-state score-state--error">
            <p className="score-error-text">{error}</p>
            <button type="button" className="score-retry-btn" onClick={load}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {activeTab === 'score' ? (
              <>
                <div className="score-display-large">
              <div className="score-number">{displayScore(tenantScore)}</div>
              {periodLabel && <div className="score-period">{periodLabel}</div>}
              <div className={`score-badge ${band.badgeClass}`}>
                <MdShield className="badge-icon" />
                <span>{band.label}</span>
              </div>
            </div>

            <div className="score-card">
              <h2 className="score-card-title">What this score means</h2>
              <p className="score-card-text">{meaningCopy(band.key)}</p>
              {data.halfLifeMonths != null && (
                <p className="score-card-meta">
                  <MdInfoOutline aria-hidden className="score-card-meta-icon" />
                  Recent months count more (half-life ~{data.halfLifeMonths} mo). Window: last 12 months of records.
                </p>
              )}
            </div>

            <div className="score-card">
              <div className="score-factors-header">
                <h2 className="score-card-title">Rent behavior</h2>
                <button type="button" className="view-history-btn" onClick={handleViewHistory}>
                  <span>View history</span>
                  <MdChevronRight className="chevron-icon" />
                </button>
              </div>

              {!hasWindow ? (
                <p className="score-empty-factors">
                  No counted rent months in this period yet. When payments are recorded, on-time, late, and missed
                  breakdown will appear here.
                </p>
              ) : (
                <>
                  <p className="score-factors-intro">
                    Based on <strong>{totalMonths}</strong> counted month{totalMonths === 1 ? '' : 's'}
                    {periodLabel ? ` (${periodLabel})` : ''}.
                  </p>
                  <div className="score-factors-list">
                    <div className="factor-row">
                      <div className="factor-icon" style={{ backgroundColor: '#2e7d32' }}>
                        <MdCheckCircle />
                      </div>
                      <div className="factor-content">
                        <div className="factor-title">On-time</div>
                        <div className="factor-subtitle">Months paid on time</div>
                      </div>
                      <div className="factor-status" style={{ color: '#2e7d32' }}>
                        {data.onTimeCount ?? 0}
                      </div>
                    </div>

                    <div className="factor-row">
                      <div className="factor-icon" style={{ backgroundColor: '#f57c00' }}>
                        <MdSchedule />
                      </div>
                      <div className="factor-content">
                        <div className="factor-title">Late</div>
                        <div className="factor-subtitle">Late payments</div>
                      </div>
                      <div className="factor-status" style={{ color: '#e65100' }}>
                        {data.lateCount ?? 0}
                      </div>
                    </div>

                    <div className="factor-row">
                      <div className="factor-icon" style={{ backgroundColor: '#c62828' }}>
                        <MdCancel />
                      </div>
                      <div className="factor-content">
                        <div className="factor-title">Missed</div>
                        <div className="factor-subtitle">Missed rent</div>
                      </div>
                      <div className="factor-status" style={{ color: '#c62828' }}>
                        {data.missedCount ?? 0}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="score-card score-booster-card">
              <h2 className="score-card-title">💡 How to Boost Your Score</h2>
              <div className="score-booster-grid">
                <div className="score-booster-item">
                  <div className="score-booster-bullet">1</div>
                  <div className="score-booster-text">
                    <strong>Pay On or Before Due Date</strong>
                    <p>On-time payments carry the highest weight in your score calculation.</p>
                  </div>
                </div>
                <div className="score-booster-item">
                  <div className="score-booster-bullet">2</div>
                  <div className="score-booster-text">
                    <strong>Maintain a Long Streak</strong>
                    <p>3+ consecutive on-time months unlocks top trust badges with landlords.</p>
                  </div>
                </div>
                <div className="score-booster-item">
                  <div className="score-booster-bullet">3</div>
                  <div className="score-booster-text">
                    <strong>Upload Prompt Payment Evidence</strong>
                    <p>Submit M-Pesa receipts right away so your ledger updates in real-time.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="score-card">
              <div className="score-factors-header">
                <h2 className="score-card-title">Tenant ratings</h2>
                <button
                  type="button"
                  className="view-history-btn"
                  onClick={() => setActiveTab('ratings')}
                  disabled={!data?.ratings || data.ratings.length === 0}
                >
                  <span>View history</span>
                  <MdChevronRight className="chevron-icon" />
                </button>
              </div>

              {data.avgLandlordRating != null && data.landlordRatingCount > 0 ? (
                <div className="rating-summary">
                  <div className="rating-stars">
                    {renderStars(data.avgLandlordRating)}
                    <span className="rating-score-text">{data.avgLandlordRating.toFixed(1)}</span>
                  </div>
                  <div className="rating-count">
                    Based on {data.landlordRatingCount} rating{data.landlordRatingCount === 1 ? '' : 's'}
                  </div>
                </div>
              ) : (
                <p className="score-empty-factors">
                  No ratings from landlords or agents yet.
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="ratings-history-tab-view">
            {data?.ratings && data.ratings.length > 0 ? (
              (() => {
                const totalPages = Math.ceil(data.ratings.length / RATINGS_PER_PAGE);
                const pageRatings = data.ratings.slice(
                  (ratingsPage - 1) * RATINGS_PER_PAGE,
                  ratingsPage * RATINGS_PER_PAGE
                );
                return (
                  <>
                    {pageRatings.map((rating) => (
                      <div key={rating.id} className="rating-item">
                        <div className="rating-item-top">
                          <div className="rating-item-stars">
                            {renderStars(rating.score)}
                          </div>
                          <div className="rating-item-date">{formatDate(rating.createdAt)}</div>
                        </div>

                        {rating.message && (
                          <div className="rating-item-message">"{rating.message}"</div>
                        )}

                        <div className="rating-item-bottom">
                          <div className="rating-item-info">
                            {rating.propertyName && (
                              <>
                                <div className="info-label">PROPERTY</div>
                                <div className="info-value property-value">{rating.propertyName}</div>
                              </>
                            )}
                          </div>

                          <div className="rating-item-reviewer">
                            <div className="info-label">REVIEWED BY</div>
                            <div className="reviewer-badge">{rating.actorName}</div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {totalPages > 1 && (
                      <div className="ratings-pagination">
                        <button
                          type="button"
                          className="pagination-btn"
                          disabled={ratingsPage === 1}
                          onClick={() => setRatingsPage((p) => p - 1)}
                        >
                          ← Previous
                        </button>
                        <span className="pagination-info">
                          Page {ratingsPage} of {totalPages}
                        </span>
                        <button
                          type="button"
                          className="pagination-btn"
                          disabled={ratingsPage === totalPages}
                          onClick={() => setRatingsPage((p) => p + 1)}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                );
              })()
            ) : (
              <div className="score-state score-state--empty">
                <p>You haven't received any ratings yet. Ratings will appear here once a landlord or agent submits feedback.</p>
              </div>
            )}
          </div>
        )}
      </>
    )}

        {!loading && !error && !data && (
          <div className="score-state score-state--empty">
            <p>No score data returned.</p>
            <button type="button" className="score-retry-btn" onClick={load}>
              Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Score;
